import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';

type TabItem = { id: string; label: string; content: React.ReactNode };

interface TabsProps {
  items: TabItem[];
  value?: string;
  onChange?: (id: string) => void;
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({ items, value, onChange, className }) => {
  const [active, setActive] = useState<string>(value ?? (items[0]?.id || ''));
  const navRef = useRef<HTMLDivElement>(null);
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
  const [fadeIn, setFadeIn] = useState(false);

  const currentActiveId = value ?? active;

  const recalc = useCallback(() => {
    const btn = btnRefs.current[currentActiveId];
    const nav = navRef.current;

    if (btn && nav) {
      setIndicator({ left: btn.offsetLeft, width: btn.offsetWidth });

      const btnRight = btn.offsetLeft + btn.offsetWidth;
      if (btn.offsetLeft < nav.scrollLeft) {
        nav.scrollLeft = btn.offsetLeft;
      } else if (btnRight > nav.scrollLeft + nav.clientWidth) {
        nav.scrollLeft = btnRight - nav.clientWidth;
      }
    }
  }, [currentActiveId, items]);

  const current = useMemo(
    () => items.find(i => i.id === currentActiveId) ?? items[0],
    [items, currentActiveId]
  );

  useEffect(() => {
    if (value !== undefined) {
      setActive(value);
    }
  }, [value]);

  useEffect(() => {
    recalc();

    setFadeIn(false);
    const r = requestAnimationFrame(() => setFadeIn(true));
    return () => cancelAnimationFrame(r);
  }, [currentActiveId, recalc]);

  useEffect(() => {
    const onResize = () => recalc();

    window.addEventListener('resize', onResize);
    onResize();

    return () => window.removeEventListener('resize', onResize);
  }, [recalc]);

  const select = (id: string) => {
    if (currentActiveId === id) return;
    if (value === undefined) {
      setActive(id);
    }
    onChange?.(id);
  };

  return (
    <div className={className}>
      <div
        ref={navRef}
        role="tablist"
        className="relative mb-4 flex overflow-x-auto border-b border-gray-200"
      >
        {items.map(it => (
          <button
            key={it.id}
            ref={(el) => { btnRefs.current[it.id] = el; }}
            type="button"
            role="tab"
            aria-selected={currentActiveId === it.id}
            onClick={() => select(it.id)}
            className={`relative whitespace-nowrap px-4 py-2 text-sm font-medium transition-colors focus:outline-none ${
              currentActiveId === it.id ? 'text-blue-600' : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            {it.label}
          </button>
        ))}
        <span
          className="pointer-events-none absolute bottom-0 h-0.5 bg-blue-600 transition-all duration-300 ease-out"
          style={{ left: indicator.left, width: indicator.width }}
        />
      </div>

      <div
        key={current?.id}
        className={`transition-all duration-300 ${
          fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'
        }`}
      >
        {current?.content}
      </div>
    </div>
  );
};